//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ContactSupport } from '@campaigns/components/shared/ContactSupport';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { getIsCastaCrfQuoteEnabled } from '@campaigns/redux/selectors/getIsCastaCrfQuoteEnabled';
import { Alert } from '@components/atoms/alert/Alert';
import { BodyText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { Modal } from '@components/molecules/modal/Modal';

interface RequestNewPurchaseCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const RequestNewPurchaseCampaignModal = ({
  isOpen,
  onClose,
  onSubmit,
}: RequestNewPurchaseCampaignModalProps) => {
  const { isRequesting, isSaveError, updateFormValues, watchFormValues } =
    useSamplingCampaign();

  // TODO: PD-304383 - Remove feature flag check once CastaCrfQuote is released
  const isCastaCrfQuoteEnabled = useSelector(getIsCastaCrfQuoteEnabled);

  const formValues = watchFormValues();

  const handleCommentChange = (value: string) => {
    updateFormValues('comments', {
      ...formValues.comments,
      submission: value,
    });
  };

  return (
    <Modal
      closeModal={onClose}
      contents={
        <Container>
          {isSaveError && (
            <Alert type='error'>
              We couldn't send your campaign request. Please try again.{' '}
              <ContactSupport
                leadingCopy='If the problem continues, email'
                requestContext={{
                  campaignName: formValues.name,
                  requestShortCode: formValues.externalId,
                }}
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_NEW_PURCHASE_MODAL_CONTACT_SUPPORT_LINK
                }
              />
              .
            </Alert>
          )}
          <BodyText>
            {isCastaCrfQuoteEnabled
              ? 'Your existing purchase option will be removed and a quote will be generated for this campaign.'
              : 'Your existing purchase option will be removed and a new purchase will be submitted with this campaign.'}
          </BodyText>
          <TextAreaInput
            label='Final note for our team (optional)'
            onChange={handleCommentChange}
            value={formValues.comments?.submission}
          />
        </Container>
      }
      isFooterEnabled
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: isCastaCrfQuoteEnabled
          ? 'Request a Quote'
          : 'Send Request',
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_NEW_PURCHASE_MODAL_CONFIRM_BUTTON,
        onClick: onSubmit,
        isDisabled: isRequesting,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onClose,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_NEW_PURCHASE_MODAL_CANCEL_BUTTON,
        isDisabled: isRequesting,
      }}
      title='Request New Purchase'
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
